import MenuItemFilterSection from "@/custom-types/MenuItemFilterSection";

export const DEALS_UI_FILTER_KEY = "On sale";

export const PRICE_RANGE_BUCKETS = {
  ABOVE_TWO_HUNDRED: "$200 and above",
  FIFTY_TO_HUNDRED: "$50 to $100",
  HUNDRED_TO_TWO_HUNDRED: "$100 to $200",
  TWENTYFIVE_TO_FIFTY: "$25 to $50",
  UNDER25: "Under $25",
};

export const PRICE_DISPLAY_RANGES: {
  [key: string]: {
    displayLabel: string;
  };
} = {
  "*-25.0": {
    displayLabel: PRICE_RANGE_BUCKETS.UNDER25,
  },
  "100.0-200.0": {
    displayLabel: PRICE_RANGE_BUCKETS.HUNDRED_TO_TWO_HUNDRED,
  },
  "200.0-*": {
    displayLabel: PRICE_RANGE_BUCKETS.ABOVE_TWO_HUNDRED,
  },
  "25.0-50.0": {
    displayLabel: PRICE_RANGE_BUCKETS.TWENTYFIVE_TO_FIFTY,
  },
  "50.0-100.0": {
    displayLabel: PRICE_RANGE_BUCKETS.FIFTY_TO_HUNDRED,
  },
};

export const PRICE_SEARCH_RANGES: {
  [key: string]: { min?: number; max?: number };
} = {
  [PRICE_RANGE_BUCKETS.UNDER25]: { max: 25 },
  [PRICE_RANGE_BUCKETS.TWENTYFIVE_TO_FIFTY]: { max: 50, min: 25 },
  [PRICE_RANGE_BUCKETS.FIFTY_TO_HUNDRED]: { max: 100, min: 50 },
  [PRICE_RANGE_BUCKETS.HUNDRED_TO_TWO_HUNDRED]: { max: 200, min: 100 },
  [PRICE_RANGE_BUCKETS.ABOVE_TWO_HUNDRED]: { min: 200 },
};

export const THC_RANGE_BUCKETS = {
  HIGH: "THC:High",
  LOW: "THC:Low",
  MEDIUM: "THC:Medium",
  NONE: "THC:None",
};

export const THC_DISPLAY_RANGES: {
  [key: string]: {
    badgeLabel: string;
    displayLabel: string;
    filterDisplayName: string;
  };
} = {
  "*-0.0": {
    badgeLabel: "0%",
    displayLabel: "No THC",
    filterDisplayName: THC_RANGE_BUCKETS.NONE,
  },
  "1.0-10.0": {
    badgeLabel: "<10%",
    displayLabel: "Low",
    filterDisplayName: THC_RANGE_BUCKETS.LOW,
  },
  "10.0-20.0": {
    badgeLabel: "10-20%",
    displayLabel: "Medium",
    filterDisplayName: THC_RANGE_BUCKETS.MEDIUM,
  },
  "20.0-*": {
    badgeLabel: ">20%",
    displayLabel: "High",
    filterDisplayName: THC_RANGE_BUCKETS.HIGH,
  },
};

export const THC_SEARCH_RANGES: {
  [key: string]: { min?: number; max?: number };
} = {
  [THC_RANGE_BUCKETS.NONE]: { max: 0 },
  [THC_RANGE_BUCKETS.LOW]: { max: 10, min: 1 },
  [THC_RANGE_BUCKETS.MEDIUM]: { max: 20, min: 10 },
  [THC_RANGE_BUCKETS.HIGH]: { min: 20 },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const createPriceFilter = (filters: any, filterKey: string) =>
  filters[filterKey].reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    (priceRangeFilters: any, filterValue: string, index: number) => {
      const { min, max } = PRICE_SEARCH_RANGES[filterValue];
      return {
        ...priceRangeFilters,
        [`filter[price][${index}][gte]`]: min,
        [`filter[price][${index}][lte]`]: max,
      };
    },
    {},
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const createTHCFilter = (filters: any, filterKey: string) =>
  filters[filterKey].reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    (thcRangeFilters: any, filterValue: string, index: number) => {
      const { min, max } = THC_SEARCH_RANGES[filterValue];
      return {
        ...thcRangeFilters,
        [`filter[thc][${index}][gte]`]: min,
        [`filter[thc][${index}][lte]`]: max,
      };
    },
    {},
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
export const mapFilterObjectToAPI = (filters?: any) =>
  Object.keys(filters).reduce((filterObj, filterKey) => {
    // * Price filters require special transformation to this form:
    // * "filter[price][0][gte]=0&filter[price][0][lte]=10"
    if (
      filterKey === MenuItemFilterSection.PriceRange &&
      Array.isArray(filters[filterKey])
    ) {
      return {
        ...filterObj,
        ...createPriceFilter(filters, filterKey),
      };
    }
    // * End special logic for Price Range Filtering

    // * THC filters require special transformation to this form:
    // * "filter[thc][0][gte]=0&filter[thc][0][lte]=10"
    if (
      filterKey === MenuItemFilterSection.THCLevel &&
      Array.isArray(filters[filterKey])
    ) {
      return {
        ...filterObj,
        ...createTHCFilter(filters, filterKey),
      };
    }
    // * End special logic for THC Range Filtering

    // * Change Deals filter key
    if (filterKey === DEALS_UI_FILTER_KEY) {
      return {
        ...filterObj,
        [`filter[${MenuItemFilterSection.Deals}]`]: "true",
      };
    }
    // *

    // * All other filters can be applied as-is
    return {
      ...filterObj,
      [`filter[${filterKey}]`]: filters[filterKey],
    };
  }, {});

// * Used for MenuFilters, we no longer need to format price/thc with lgt/gte separate filters.
// * Api expects the filter value to be the same as it was returned.
export const mapFilterObjectToAPIV2 = (filters: {
  [key: string]: string | boolean | number;
}) =>
  Object.keys(filters).reduce((filterObj, filterKey) => {
    if (filterKey === DEALS_UI_FILTER_KEY) {
      return {
        ...filterObj,
        [`filter[${MenuItemFilterSection.Deals}]`]: "true",
      };
    }
    return {
      ...filterObj,
      [`filter[${filterKey}]`]: filters[filterKey],
    };
  }, {});
